import { getToken, setToken, removeToken } from '../utils/tokenStorage';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell';
import MembersTab from '../components/dashboard/MembersTab';
import SavedTab from '../components/dashboard/SavedTab';
import GroupDirectoryTab from '../components/dashboard/GroupDirectoryTab';
import MyGroupsTab from '../components/dashboard/MyGroupsTab';
import MessagesTab from '../components/dashboard/MessagesTab';
import CommunityTab from '../components/dashboard/CommunityTab';
import ForumTab from '../components/dashboard/ForumTab';
import ProfileAccount, { PROFILE_SUBTABS } from '../components/dashboard/ProfileAccount';
import BondLoanTab from '../components/dashboard/BondLoanTab';
import SuburbAlertsTab from '../components/dashboard/SuburbAlertsTab';
import SupportTab from '../components/dashboard/SupportTab';
import './MemberDashboard.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEMBER_TAB_KEYS = ['profile', 'browse', 'saved', 'group-directory', 'groups', 'messages', 'community', 'forum', 'bond-loan', 'suburb-alerts', 'support'];

const MemberDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    return MEMBER_TAB_KEYS.includes(requestedTab) ? requestedTab : 'profile';
  });
  const [profileSubTab, setProfileSubTab] = useState('identity');
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  useEffect(() => {
    if (!profileMenuOpen) return;
    const close = (e) => { if (e.type === 'keydown' && e.key !== 'Escape') return; setProfileMenuOpen(false); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', close); };
  }, [profileMenuOpen]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // Shared state
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [shortlists, setShortlists] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [groups, setGroups] = useState([]);
  const [appStatus, setAppStatus] = useState(null);
  const [pendingMessageTarget, setPendingMessageTarget] = useState(null);


  // Member filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ housingType: '', state: '' });

  const getAuthConfig = () => {
    const token = getToken();
    return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  // Data fetching
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchAll = async () => {
      const config = getAuthConfig();
      try {
        const [userRes, membersRes, propsRes, shortRes, favRes, groupsRes, appRes] = await Promise.allSettled([
          axios.get(`${API}/auth/me`, config),
          axios.get(`${API}/members`, config),
          axios.get(`${API}/properties`),
          axios.get(`${API}/shortlists`, config),
          axios.get(`${API}/favorites`, config),
          axios.get(`${API}/groups`, config),
          axios.get(`${API}/auth/my-application-status`, config),
        ]);
        if (userRes.status === 'fulfilled') {
          const u = userRes.value.data;
          setCurrentUser(u);
          // Admins belong on /admin, not the member dashboard
          if (u?.role === 'admin') {
            navigate('/admin', { replace: true });
            return;
          }
        }
        if (membersRes.status === 'fulfilled') setMembers(membersRes.value.data);
        if (shortRes.status === 'fulfilled') setShortlists(shortRes.value.data);
        if (favRes.status === 'fulfilled') setFavorites(favRes.value.data);
        if (groupsRes.status === 'fulfilled') setGroups(groupsRes.value.data);
        if (appRes.status === 'fulfilled') setAppStatus(appRes.value.data);
      } catch (e) { console.error('Fetch error:', e); }
      setLoading(false);
    };
    fetchAll();
  }, []);

  // Member filtering
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let filtered = [...members];
    if (searchTerm) filtered = filtered.filter(m => m.name.toLowerCase().includes(searchTerm.toLowerCase()) || m.email?.toLowerCase().includes(searchTerm.toLowerCase()));
    if (filters.housingType) filtered = filtered.filter(m => m.application_data?.shared_housing_type === filters.housingType);
    if (filters.state) filtered = filtered.filter(m => m.application_data?.state === filters.state);
    setFilteredMembers(filtered);
  }, [members, searchTerm, filters]);

  // Favorites helpers
  const toggleFavorite = async (itemId, itemType) => {
    try {
      const existing = favorites.find(f => f.item_id === itemId && f.item_type === itemType);
      if (existing) {
        await axios.delete(`${API}/favorites/${existing.favorite_id}`, getAuthConfig());
        setFavorites(favorites.filter(f => f.favorite_id !== existing.favorite_id));
      } else {
        await axios.post(`${API}/favorites`, { item_id: itemId, item_type: itemType }, getAuthConfig());
        const res = await axios.get(`${API}/favorites`, getAuthConfig());
        setFavorites(res.data);
      }
    } catch (e) { console.error('Favorite toggle failed:', e); }
  };
  const isFavorited = (itemId, itemType) => favorites.some(f => f.item_id === itemId && f.item_type === itemType);
  const removeFavorite = async (favoriteId) => {
    try { await axios.delete(`${API}/favorites/${favoriteId}`, getAuthConfig()); setFavorites(favorites.filter(f => f.favorite_id !== favoriteId)); } catch (e) { console.error(e); }
  };

  // Shortlist helpers
  const addToShortlist = async (userId) => {
    try { await axios.post(`${API}/shortlists?shortlisted_user_id=${userId}`, {}, getAuthConfig()); alert('Added to your shortlist!'); const res = await axios.get(`${API}/shortlists`, getAuthConfig()); setShortlists(res.data); } catch (e) { alert('Failed to add to shortlist'); }
  };
  const removeFromShortlist = async (shortlistId) => {
    try { await axios.delete(`${API}/shortlists/${shortlistId}`, getAuthConfig()); alert('Removed from shortlist'); setShortlists(shortlists.filter(s => s.shortlist_id !== shortlistId)); } catch (e) { console.error(e); }
  };
  const isShortlisted = (userId) => shortlists.some(sl => sl.member.user_id === userId);

  // Group helpers
  const fetchGroups = async () => { try { const r = await axios.get(`${API}/groups`, getAuthConfig()); setGroups(r.data); } catch (e) { console.error('fetchGroups error:', e); } };
  const joinGroup = async (groupId) => { try { await axios.post(`${API}/groups/${groupId}/join`, {}, getAuthConfig()); fetchGroups(); } catch (e) { alert(e.response?.data?.detail || 'Failed to join group'); } };
  const leaveGroup = async (groupId) => { try { await axios.post(`${API}/groups/${groupId}/leave`, {}, getAuthConfig()); fetchGroups(); } catch (e) { alert('Failed to leave group'); } };
  const deleteGroup = async (groupId) => { if (!window.confirm('Delete this group?')) return; try { await axios.delete(`${API}/groups/${groupId}`, getAuthConfig()); fetchGroups(); } catch (e) { alert('Failed to delete group'); } };

  // Cross-tab: start message from members tab
  const startNewMessage = (userId) => { setActiveTab('messages'); setPendingMessageTarget(userId); };

  const clearFilters = () => { setSearchTerm(''); setFilters({ housingType: '', state: '' }); };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) { console.error("Logout error:", e); }
    removeToken();
    navigate('/');
  };

  return (
    <div className="member-dashboard" data-testid="member-dashboard">
      <header className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <img src="/logo.png" alt="House Sharing Seniors" className="dashboard-logo" />
              <div>
                <h1>Member Dashboard</h1>
                {currentUser && <p className="welcome-text">Welcome, {currentUser.name}!{currentUser.membership_class === 'gold_envoy' && <span style={{ marginLeft: '10px', background: 'linear-gradient(135deg, #b45309, #d97706)', color: 'white', fontSize: '12px', fontWeight: '900', padding: '3px 10px', borderRadius: '12px', letterSpacing: '0.05em' }}>★ GOLD ENVOY</span>}</p>}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <NotificationBell />
              <button className="btn btn-secondary" onClick={handleLogout} data-testid="logout-btn">Logout</button>
            </div>
          </div>
        </div>
      </header>

      <div className="container">
        <div className="tab-navigation">
          <div style={{ position: 'relative' }}>
            <button
              className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setActiveTab('profile'); setProfileMenuOpen(o => !o); }}
              data-testid="profile-account-menu-btn">
              Profile / Account ▾
            </button>
            {profileMenuOpen && (
              <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 900, background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 10px 30px rgba(0,0,0,0.12)', minWidth: '240px', padding: '6px', marginTop: '4px' }} data-testid="profile-account-menu">
                {PROFILE_SUBTABS.map(({ key, label }) => (
                  <button key={key}
                    onClick={() => { setActiveTab('profile'); setProfileSubTab(key); setProfileMenuOpen(false); }}
                    data-testid={`profile-menu-${key}`}
                    style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: profileSubTab === key ? '#f3f4f6' : 'transparent', padding: '9px 12px', fontSize: '13px', color: '#1a2332', borderRadius: '6px', cursor: 'pointer', fontWeight: profileSubTab === key ? 700 : 500 }}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {[
            { key: 'browse', label: 'Members' },
            { key: 'saved', label: `Saved (${favorites.length + shortlists.length})` },
            { key: 'group-directory', label: 'Group Directory' },
            { key: 'groups', label: `My Groups (${groups.length})` },
            { key: 'messages', label: 'Messages' },
            { key: 'community', label: 'Community Chat' },
            { key: 'forum', label: 'Forum' },
            { key: 'bond-loan', label: 'Free Bond Loan' },
            { key: 'suburb-alerts', label: 'Suburb Alerts' },
            { key: 'support', label: 'Support' },
          ].map(({ key, label }) => (
            <button key={key} className={`tab-btn ${activeTab === key ? 'active' : ''}`}
              onClick={() => { setActiveTab(key); setProfileMenuOpen(false); }} data-testid={`${key === 'browse' ? 'browse' : key}-tab`}>
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'browse' && (
          <MembersTab filteredMembers={filteredMembers} loading={loading} searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            filters={filters} setFilters={setFilters} clearFilters={clearFilters} currentUser={currentUser}
            toggleFavorite={toggleFavorite} isFavorited={isFavorited} isShortlisted={isShortlisted} addToShortlist={addToShortlist}
            startNewMessage={startNewMessage} navigate={navigate} />
        )}
        {activeTab === 'saved' && (
          <SavedTab favorites={favorites} shortlists={shortlists} removeFavorite={removeFavorite}
            removeFromShortlist={removeFromShortlist} setActiveTab={setActiveTab} navigate={navigate} />
        )}
        {activeTab === 'group-directory' && <GroupDirectoryTab currentUser={currentUser} navigate={navigate} />}
        {activeTab === 'groups' && (
          <MyGroupsTab groups={groups} currentUser={currentUser} joinGroup={joinGroup} leaveGroup={leaveGroup} deleteGroup={deleteGroup} navigate={navigate} />
        )}
        {activeTab === 'messages' && (
          <MessagesTab currentUser={currentUser} getAuthConfig={getAuthConfig}
            pendingMessageTarget={pendingMessageTarget} clearPendingTarget={() => setPendingMessageTarget(null)} />
        )}
        {activeTab === 'community' && <CommunityTab currentUser={currentUser} />}
        {activeTab === 'forum' && <ForumTab currentUser={currentUser} getAuthConfig={getAuthConfig} />}
        {activeTab === 'bond-loan' && <BondLoanTab currentUser={currentUser} />}
        {activeTab === 'suburb-alerts' && <SuburbAlertsTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'support' && <SupportTab />}
        {activeTab === 'profile' && (
          <ProfileAccount currentUser={currentUser} setCurrentUser={setCurrentUser} appStatus={appStatus}
            getAuthConfig={getAuthConfig} subTab={profileSubTab} setSubTab={setProfileSubTab} />
        )}
      </div>
    </div>
  );
};

export default MemberDashboard;
